import { useLocalStorage } from '@mantine/hooks'
import { useCallback, useMemo } from 'react'
import { joinURL } from 'ufo'
import {
  type OpenSignModalEventData,
  openSignModal,
} from 'src/components/Account/utils'
import { useMyInfo } from 'src/services/apollo/auth'
import type { User } from 'src/services/apollo/generated/graphql'
import { useMe } from 'src/services/helpers/user'
import { getMayTokenBeValid } from 'src/utils/token'

export {
  UserSubscriptionStatus,
  userHasMembership,
} from 'src/utils/userMembership'

export function isAdmin(user?: User) {
  return Boolean(user?.isAdmin)
}

export function isUserBlocked(user?: User) {
  return (user?.blockStatus && user?.blockStatus !== 'none') || false
}

export function userHasNotVerifiedEmail(user?: User) {
  return (user && user.email && !user.emailVerified) || false
}

const SHOW_USER_EMAIL_VERIFICATION_DOT_KEY =
  'growth:show-user-email-verification-dot'

export function useShowEmailVerificationDot(
  dotLocation: 'account-btn' | 'account-menu' | 'user-profile-btn',
  user?: User,
) {
  const [rawVal, setVal] = useLocalStorage({
    key: SHOW_USER_EMAIL_VERIFICATION_DOT_KEY,
    defaultValue: ['account-btn', 'account-menu', 'user-profile-btn'],
  })

  const setter = useCallback(() => {
    setVal(prev => {
      const newSet = new Set(prev)
      newSet.delete(dotLocation)
      return [...newSet]
    })
  }, [setVal, dotLocation])

  const userNeedsVerify = userHasNotVerifiedEmail(user)

  const val = useMemo(() => {
    if (!userNeedsVerify) return false
    return rawVal ? rawVal?.includes(dotLocation) : true
  }, [userNeedsVerify, rawVal, dotLocation])

  return [val, setter] as const
}

/**
 * Ensure a user is logged in
 *
 * @returns A promise which will be resolved when a user is logged in
 *          successfully. It rejects when the user give up logging in.
 */
export async function ensureLogIn(eventData?: OpenSignModalEventData) {
  if (!getMayTokenBeValid()) {
    await new Promise((resolve, reject) => {
      openSignModal(
        {
          onSuccess: resolve,
          onClose: () => {
            if (!getMayTokenBeValid()) {
              reject()
            }
          },
        },
        eventData,
      )
    })
  }
}

/**
 * Create a profile URL for a user
 *
 * @example "/@pixai-official"
 */
export function createProfileUrl(user: User, full?: boolean) {
  const pathname = `/@${user.username}`
  return full ? joinURL(window.location.origin, pathname) : pathname
}

export function useUserIsRevivable() {
  const { data: userInfo } = useMyInfo()
  const canRevive =
    userInfo?.deleteAfter &&
    new Date(userInfo.deleteAfter).getTime() > new Date().getTime()

  return canRevive
}

export function useCanUsePrivateChannel() {
  const { me } = useMe()
  return (me?.membership?.tier ?? 0) >= 2
}

export const getUserDisplayName = (
  user?: Pick<User, 'displayName' | 'username'>,
) => {
  return user?.displayName || user?.username
}
